/**
 * ステージ一覧を地方ごとにまとめる（純粋ロジック / DOM 非依存）。
 *
 * 県選びの画面（トップ）とゲーム中の地域選択モーダルの両方が同じ並びを使う。
 * 47県まで増えるので、並びの決まりごとは1か所にしか置かない。
 */

import { AREAS, areaOf } from '../game/config/regions';
import { STAGES } from '../game/config/stages';

/** 地方ごとのかたまり。 */
export interface StageGroup<T> {
  /** 例: '九州・沖縄'。 */
  name: string;
  /** 都道府県コード順に並べ直したステージ。 */
  items: T[];
}

/**
 * 地方ごとにまとめ、地方のなかは都道府県コード順に並べ直す。
 *
 * まだ1県も無い地方は見出しごと出さない（空の見出しが並ぶほうが分かりにくい）。
 * 地方の表に無い regionId（ご当地ステージなど）は「そのほか」に最後にまとめる。
 */
export function groupByArea<T extends { id: string }>(summaries: readonly T[]): StageGroup<T>[] {
  const byArea = new Map<string, T[]>();
  const others: T[] = [];

  for (const summary of summaries) {
    const regionId = STAGES[summary.id]?.regionId ?? '';
    const area = areaOf(regionId);
    if (!area) {
      others.push(summary);
      continue;
    }
    const list = byArea.get(area.id);
    if (list) list.push(summary);
    else byArea.set(area.id, [summary]);
  }

  const groups: StageGroup<T>[] = [];
  for (const area of AREAS) {
    const list = byArea.get(area.id) ?? [];
    if (list.length === 0) continue;
    list.sort(
      (a, b) =>
        area.regionIds.indexOf(STAGES[a.id]?.regionId ?? '') -
        area.regionIds.indexOf(STAGES[b.id]?.regionId ?? ''),
    );
    groups.push({ name: area.name, items: list });
  }
  if (others.length > 0) groups.push({ name: 'そのほか', items: others });
  return groups;
}
